import { STORY_COMPANY } from '../data/shefira.js';
import { ringDistance, tileDistance } from '../world/grid.js';
/**
 * The rings an ally stands in: beside her for a blade, a few tiles off for a
 * bow or a staff. Enraged, the ring of fire spares `safe` rings round her
 * home: a ranged ally keeps inside it.
 */
export function allyBand(ally, safe = null) {
    const band = ally.stance === 'melee' ? STORY_COMPANY.meleeBand : STORY_COMPANY.rangedBand;
    if (safe === null)
        return { ...band };
    const max = Math.max(band.min, Math.min(band.max, safe - 1));
    return { min: Math.min(band.min, max), max };
}
/** Whether an ally strikes her from where it stands. */
export function allyReaches(ally, from, her) {
    const reach = ally.stance === 'melee' ? STORY_COMPANY.meleeReach : STORY_COMPANY.rangedReach;
    return ringDistance(from, her) <= reach;
}
/**
 * Where an ally should stand round `centre` (her, or a post): its own tile
 * while it lies in the band and is free, else the goal it walks to while that
 * still does, else the free tile of the band nearest it. Ties fall to a fixed
 * order (north first, then west), so the choice is the same on every look and
 * the ally does not dither. Null when the whole band is taken.
 *
 * `isFree` says whether it may end there: open, stood on by nobody else,
 * claimed by no other ally, and outside every shape she marked.
 */
export function allyPlace(centre, from, band, isFree, goal) {
    const inBand = (tile) => {
        const ring = ringDistance(tile, centre);
        return ring >= band.min && ring <= band.max;
    };
    if (inBand(from) && isFree(from))
        return from;
    if (goal !== undefined && inBand(goal) && isFree(goal))
        return goal;
    let best = null;
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let dy = -band.max; dy <= band.max; dy += 1) {
        for (let dx = -band.max; dx <= band.max; dx += 1) {
            const tile = { x: centre.x + dx, y: centre.y + dy };
            if (!inBand(tile))
                continue;
            const distance = tileDistance(from, tile);
            if (distance >= bestDistance || !isFree(tile))
                continue;
            best = tile;
            bestDistance = distance;
        }
    }
    return best;
}
/**
 * The share of its health an ally has at `herShare` of hers: whole at her
 * whole, `lastStand` at its fall, in a straight line between. Past its fall
 * it stays at `lastStand`: the fall itself takes the rest.
 */
export function allyHealthShare(herShare, fallsAt) {
    const last = STORY_COMPANY.lastStand;
    if (fallsAt >= 1)
        return last;
    const k = Math.max(0, Math.min(1, (herShare - fallsAt) / (1 - fallsAt)));
    return last + (1 - last) * k;
}
/**
 * What her burns take from an ally standing above its pace: nothing while
 * within `slack` of it, else down to it. Blows are whole points and never
 * the last one: its fall is the story's to give.
 */
export function allyScorch(hp, maxHp, herShare, fallsAt) {
    if (maxHp <= 0 || hp <= 1)
        return 0;
    const pace = Math.round(maxHp * allyHealthShare(herShare, fallsAt));
    if (hp - pace <= maxHp * STORY_COMPANY.slack)
        return 0;
    return Math.max(0, Math.min(hp - 1, hp - pace));
}
/**
 * What a shape or her ring deals an ally caught in it: the blow as it comes
 * (`raw`, halved by a frost veil before), but never carrying it more than
 * `lead` below its pace, never under its last stand, and never killing it.
 */
export function allyWound(hp, maxHp, raw, herShare, fallsAt) {
    if (maxHp <= 0 || hp <= 1 || raw <= 0)
        return 0;
    const share = allyHealthShare(herShare, fallsAt);
    const floor = Math.max(1, Math.round(maxHp * Math.max(STORY_COMPANY.lastStand, share - STORY_COMPANY.lead)));
    return Math.max(0, Math.min(Math.round(raw), hp - floor));
}
//# sourceMappingURL=story-allies.js.map